import React, { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { useStudentKeyQuery } from '@hook/iyagigo/useApi';
import { useStudentKey } from '@hook/iyagigo/useInit';
import { EApiLanguageCode, IStudyConfigurationData } from '@datatype/iyagigo/api-type';
import Spinner from '@design-system/spinner';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';

const LoadingScreen = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${color.WHITE_1};
`;

const Loading = () => {
  const [showFallbackMessage, setShowFallbackMessage] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => setShowFallbackMessage(true), 3000);
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <LoadingScreen>
      <Spinner size='32px' color={color.BROWN_1} />
      {showFallbackMessage && (
        <>
          <Gap height={size.MARGIN_2} />
          <TextBlock
            text='앱이 멈춘 경우, 앱을 다시 시작해 주세요'
            design={{ text: { size: 'medium', color: color.GRAY_1, bold: false } }}
          />
        </>
      )}
    </LoadingScreen>
  );
};

export const isStudyPlanDone = (studyConfiguration?: IStudyConfigurationData | null) => {
  if (!studyConfiguration?.sourceChannel) return false;

  if (studyConfiguration.studyLanguage === EApiLanguageCode.ENGLISH) {
    return (
      Boolean(studyConfiguration.englishStudyMotivation?.length) &&
      Boolean(studyConfiguration.englishLanguageLevel) &&
      Boolean(studyConfiguration.englishStudyTimeGoal)
    );
  }

  if (studyConfiguration.studyLanguage === EApiLanguageCode.JAPANESE) {
    return (
      Boolean(studyConfiguration.japaneseStudyMotivation?.length) &&
      Boolean(studyConfiguration.japaneseLanguageLevel) &&
      Boolean(studyConfiguration.japaneseStudyTimeGoal)
    );
  }

  return false;
};

const ServicePageGuard: React.FC = () => {
  const { studentKey, studentKeySyncing } = useStudentKey();
  const studentQuery = useStudentKeyQuery();
  const studyConfiguration = studentQuery.data?.data?.studyConfiguration;
  const isStudentLoading = !studentQuery.data?.data || !studentQuery.isFetchedAfterMount;

  // 로그인 회원의 /student/me 조회와 localStorage studentKey 동기화를 기다린다.
  if (studentKeySyncing) return <Loading />;
  // /student/{studentKey} 조회는 studentKey가 있을 때만 실행된다.
  if (!studentKey) return <Navigate replace to={IYAGIGO_PATH.STUDY_PLAN_PATH} />;
  // persisted cache만 복원된 상태로 온보딩 여부를 판단하지 않도록 최초 서버 응답까지 기다린다.
  // 이후 background refetch의 isFetching은 Outlet을 내리지 않는다.
  if (isStudentLoading) return <Loading />;
  if (!isStudyPlanDone(studyConfiguration)) {
    return <Navigate replace to={IYAGIGO_PATH.STUDY_PLAN_PATH} />;
  }

  return <Outlet />;
};

export default ServicePageGuard;
